import { useEffect } from "react";

export const SITE_NAME = "WalletRecover";
export const SITE_URL = "https://walletrecover.xyz";
export const PUBLISHER_NAME = "WalletRecover Team";

export interface SeoMetaOptions {
  title: string;
  description?: string;
  path: string; // e.g. "/blog/foo" — used to build the canonical + og:url
  type?: "website" | "article";
  image?: string;
  publishedTime?: string; // ISO date, articles only
  jsonLd?: Record<string, unknown>;
}

interface RestoreEntry {
  el: Element;
  created: boolean;
  attr: string;
  prev: string | null;
}

function upsertMeta(selector: string, attr: string, attrValue: string, content: string): RestoreEntry {
  let el = document.querySelector<HTMLMetaElement>(selector);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute("content");
  el.setAttribute("content", content);
  return { el, created, attr: "content", prev };
}

function upsertLink(rel: string, href: string): RestoreEntry {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  let created = false;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute("href");
  el.setAttribute("href", href);
  return { el, created, attr: "href", prev };
}

/**
 * Per-page SEO: title, description, canonical URL, Open Graph + Twitter
 * card tags, and an optional JSON-LD block (BlogPosting/NewsArticle).
 * Restores whatever was there before on unmount, same approach as the
 * simpler useDocumentMeta this supersedes for content pages.
 */
export function useSeoMeta(opts: SeoMetaOptions): void {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${opts.title} — ${SITE_NAME}`;
    const url = `${SITE_URL}${opts.path}`;
    const entries: RestoreEntry[] = [];

    if (opts.description) {
      entries.push(upsertMeta('meta[name="description"]', "name", "description", opts.description));
      entries.push(upsertMeta('meta[property="og:description"]', "property", "og:description", opts.description));
      entries.push(upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", opts.description));
    }

    entries.push(upsertMeta('meta[property="og:title"]', "property", "og:title", opts.title));
    entries.push(upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", opts.title));
    entries.push(upsertMeta('meta[property="og:type"]', "property", "og:type", opts.type ?? "website"));
    entries.push(upsertMeta('meta[property="og:url"]', "property", "og:url", url));
    entries.push(upsertMeta('meta[property="og:site_name"]', "property", "og:site_name", SITE_NAME));
    entries.push(
      upsertMeta(
        'meta[name="twitter:card"]',
        "name",
        "twitter:card",
        opts.image ? "summary_large_image" : "summary"
      )
    );

    if (opts.image) {
      entries.push(upsertMeta('meta[property="og:image"]', "property", "og:image", opts.image));
      entries.push(upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", opts.image));
    }

    if (opts.publishedTime) {
      entries.push(
        upsertMeta(
          'meta[property="article:published_time"]',
          "property",
          "article:published_time",
          opts.publishedTime
        )
      );
    }

    entries.push(upsertLink("canonical", url));

    let jsonLdEl: HTMLScriptElement | null = null;
    if (opts.jsonLd) {
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.textContent = JSON.stringify(opts.jsonLd);
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = prevTitle;
      for (const entry of entries) {
        if (entry.created) entry.el.remove();
        else if (entry.prev !== null) entry.el.setAttribute(entry.attr, entry.prev);
      }
      jsonLdEl?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.title, opts.description, opts.path, opts.type, opts.image, opts.publishedTime, JSON.stringify(opts.jsonLd)]);
}
